// src/components/ui/Table/Table.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from './Pagination';
import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';
import { useResponsive } from '@/hooks/useResponsive'; 
import { Card } from '../Layout/Card';
import styles from './Table.module.css';

export interface TableColumn<T> {
  key: string;
  title: React.ReactNode;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'auto';
  render?: (item: T, index: number) => React.ReactNode;
  textAlign?: 'left' | 'center' | 'right';
  wrap?: boolean;
  truncate?: boolean;
  sortable?: boolean;
  maxLength?: number;
  resizable?: boolean;
  minWidth?: number;
  dataType?: 'text' | 'number' | 'currency' | 'percent' | 'date' | 'datetime';
  // Свойства для мобильных устройств
  mobilePriority?: number; 
  hideOnMobile?: boolean;
  mobileTitle?: React.ReactNode; // Альтернативный заголовок для мобильных
  mobileIcon?: React.ReactNode; // Иконка для мобильного представления
}

export interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  className?: string;
  onRowClick?: (item: T, index: number) => void;
  isLoading?: boolean;
  keyExtractor?: (item: T, index: number) => string | number;
  emptyText?: string;
  loadingText?: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  maxHeight?: string | number;
  scrollable?: boolean;
  // Параметры для пагинации
  currentPage?: number;
  pageSize?: number;
  totalItems?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  showPagination?: boolean;
  showPageSizeSelector?: boolean;
  itemName?: string;
  paginationAddons?: React.ReactNode;
  // Параметры для изменяемых размеров столбцов
  resizableColumns?: boolean;
  savedColumnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  // Параметр для контроля адаптивности
  responsive?: boolean;
  autoHeight?: boolean;
  compactMode?: boolean;
  // Новые параметры для адаптивного представления
  mobileBehavior?: 'cards' | 'table' | 'scroll';
  cardClassName?: string;
  hideTableHeaderOnMobile?: boolean;
  cardLabelWidth?: string | number;
  // Можно передать кастомный рендер для карточек
  renderMobileCard?: (item: T, index: number) => React.ReactNode;
}

export function Table<T>({
  data,
  columns,
  className = '',
  onRowClick,
  isLoading = false,
  keyExtractor = (_, index) => index,
  emptyText,
  loadingText,
  sortColumn,
  sortDirection,
  onSort,
  maxHeight = '600px',
  scrollable = true,
  // Параметры для пагинации
  currentPage = 1,
  pageSize = 10,
  totalItems,
  totalPages,
  onPageChange,
  onPageSizeChange,
  showPagination = false,
  showPageSizeSelector = false,
  itemName,
  paginationAddons,
  // Параметры для изменяемых размеров столбцов
  resizableColumns = false,
  savedColumnWidths = {},
  onColumnWidthsChange,
  // Параметр для контроля адаптивности
  responsive = true,
  autoHeight = false,
  compactMode,
  // Новые параметры для адаптивного представления
  mobileBehavior = 'table',
  cardClassName = '',
  hideTableHeaderOnMobile = true,
  cardLabelWidth = '40%',
  renderMobileCard
}: TableProps<T>) {
  // Используем хук для переводов
  const { t } = useTranslation('ui');
  
  // Используем хук для получения информации об устройстве и размерах экрана
  const { 
    isMobile, 
    isTablet, 
    width: screenWidth  } = useResponsive();
  
  // Определяем, нужно ли использовать компактный режим отображения
  const useCompactMode = compactMode !== undefined 
    ? compactMode
    : (isMobile || (isTablet && screenWidth < 768));
  
  // Определяем, нужно ли использовать карточный режим
  const useCardMode = isMobile && mobileBehavior === 'cards';
  
  // Определяем, нужно ли скрывать заголовок таблицы на мобильных
  const hideHeader = isMobile && hideTableHeaderOnMobile && mobileBehavior === 'table';
  
  // Определяем, нужно ли включить горизонтальную прокрутку
  const enableHorizontalScroll = isMobile && mobileBehavior === 'scroll';

  // Состояние для хранения ширины столбцов
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(savedColumnWidths);
  
  // Ref для получения доступа к контейнеру таблицы
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  // Состояние для отслеживания размера контейнера
  const [containerWidth, setContainerWidth] = useState(0);

  // Определяем видимые колонки на основе размера экрана
  const getVisibleColumns = () => {
    // Для мобильных устройств в режиме таблицы фильтруем колонки
    if (isMobile && mobileBehavior === 'table') {
      return columns.filter(column => {
        // Пропускаем колонки, которые должны быть скрыты на мобильных
        if (column.hideOnMobile) return false;
  
        // Оставляем только колонки с высоким приоритетом
        if (column.mobilePriority !== undefined) {
          return column.mobilePriority <= 2; // Показываем только с приоритетом 0, 1, 2
        }
  
        return true; // По умолчанию показываем
      });
    }
    
    // Для планшетов фильтруем меньше колонок
    if (isTablet) {
      return columns.filter(column => {
        if (column.hideOnMobile) return false;
        
        if (column.mobilePriority !== undefined) {
          return column.mobilePriority <= 3; // Показываем с приоритетом 0, 1, 2, 3
        }
        
        return true;
      });
    }
    
    // По умолчанию показываем все колонки
    return columns;
  };
  
  // Получаем видимые колонки
  const visibleColumns = getVisibleColumns();

  // ИСПРАВЛЕНО: Упрощенная и более надежная логика расчета totalPages и totalItems
  const displayedDataLength = data.length;
  
  // Если totalItems передан извне, используем его, иначе используем длину данных
  const effectiveTotalItems = totalItems !== undefined ? totalItems : displayedDataLength;
  
  // Рассчитываем количество страниц, используя totalPages, если он передан, иначе вычисляем
  const calculatedTotalPages = totalPages !== undefined 
    ? totalPages 
    : Math.max(1, Math.ceil(effectiveTotalItems / pageSize));
  
  // Определяем, нужно ли показывать пагинацию
  const shouldShowPagination = showPagination && calculatedTotalPages > 1 && onPageChange;
  
  // ИСПРАВЛЕНО: отладочная информация о пагинации
  console.log('Table - pagination data:', {
    effectiveTotalItems,
    calculatedTotalPages,
    currentPage,
    pageSize,
    shouldShowPagination,
    showPagination
  });
  
  // Данные для текущей страницы при внутренней пагинации
  // ИСПРАВЛЕНО: Используем встроенную разбивку только если не переданы конкретные данные
  const displayedData = data;

  // Обработка изменения ширины столбца
  const handleColumnResize = (columnKey: string, width: number) => {
    const newWidths = {
      ...columnWidths,
      [columnKey]: width
    };
    
    setColumnWidths(newWidths);
    
    if (onColumnWidthsChange) {
      onColumnWidthsChange(newWidths);
    }
  };

  // Обработка клика по заголовку для сортировки
  const handleSort = (column: string) => {
    if (!onSort || !column) return;
    
    const newDirection = column === sortColumn && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(column, newDirection);
  };

  // Применяем сохраненные значения ширины при инициализации или изменении
  useEffect(() => {
    if (Object.keys(savedColumnWidths).length > 0) {
      setColumnWidths(savedColumnWidths);
    }
  }, [savedColumnWidths]);

  // Эффект для отслеживания изменения размера контейнера
  useEffect(() => {
    if (!responsive || !tableContainerRef.current) return;

    const updateContainerWidth = () => {
      if (tableContainerRef.current) {
        const rect = tableContainerRef.current.getBoundingClientRect();
        const width = rect ? rect.width : 0;
        setContainerWidth(width);
      }
    };

    // Вызываем сразу для начальной инициализации
    updateContainerWidth();

    // Создаем ResizeObserver для отслеживания изменений размера
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateContainerWidth);
      resizeObserver.observe(tableContainerRef.current);
      
      return () => {
        if (tableContainerRef.current) {
          resizeObserver.unobserve(tableContainerRef.current);
        }
      };
    }

    // Запасной вариант, если ResizeObserver не поддерживается
    window.addEventListener('resize', updateContainerWidth);
    return () => {
      window.removeEventListener('resize', updateContainerWidth);
    };
  }, [responsive]);

  // Определяем стиль контейнера таблицы
  const getTableContainerStyle = () => {
    if (autoHeight) {
      return {};
    } else if (scrollable) {
      return { 
        maxHeight: isMobile ? '400px' : maxHeight
      };
    } else {
      return {};
    }
  };

  // Рендер карточек для мобильного режима
  const renderCards = () => {
    if (isLoading) {
      return (
        <Card className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <span>{loadingText || t('table.loading', { defaultValue: 'Загрузка...' })}</span>
        </Card>
      );
    }
    
    if (!data || data.length === 0) {
      return (
        <Card className={styles.emptyContainer}>
          {emptyText || t('table.empty', { defaultValue: 'Нет данных' })}
        </Card>
      );
    }
    
    // Используем пользовательский рендер, если он предоставлен
    if (renderMobileCard) {
      return (
        <div className={styles.cardContainer}>
          {displayedData.map((item, index) => (
            <div 
              key={keyExtractor(item, index)}
              onClick={() => onRowClick && onRowClick(item, index)}
              className={onRowClick ? styles.cardClickable : ''}
            >
              {renderMobileCard(item, index)}
            </div>
          ))}
        </div>
      );
    }
    
    // Фильтруем только важные колонки для отображения в карточках
    const cardColumns = columns.filter(col => 
      !col.hideOnMobile && 
      (col.mobilePriority === undefined || col.mobilePriority <= 4)
    );
    
    // Стандартный рендер карточек
    return (
      <div className={styles.cardContainer}>
        {displayedData.map((item, index) => (
          <Card 
            key={keyExtractor(item, index)}
            className={`${styles.card} ${cardClassName} ${onRowClick ? styles.cardClickable : ''}`}
            onClick={() => onRowClick && onRowClick(item, index)}
          >
            <div>
              {cardColumns.map((column) => {
                // Получаем значение для ячейки
                const value = column.render 
                  ? column.render(item, index) 
                  : (item as any)[column.key] ?? '—';
                  
                // Заголовок для мобильной версии
                const title = column.mobileTitle || column.title;
                
                return (
                  <div 
                    key={column.key as string}
                    className={styles.cardField}
                  >
                    <div 
                      style={{ width: cardLabelWidth }} 
                      className={styles.cardLabel}
                    >
                      {column.mobileIcon && <span className={styles.cardIcon}>{column.mobileIcon}</span>}
                      {title}:
                    </div>
                    <div className={styles.cardValue}>
                      {value}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  // ИСПРАВЛЕНО: Обработчики для пагинации, которые не зависят от isLoading
  const handlePaginationPageChange = (page: number) => {
    console.log(`Table: Запрос на изменение страницы на ${page}`);
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const handlePaginationSizeChange = (size: number) => {
    console.log(`Table: Запрос на изменение размера страницы на ${size}`);
    if (onPageSizeChange) {
      onPageSizeChange(size);
    }
  };

  // Карточный режим для мобильных устройств
  if (useCardMode) {
    return (
      <div className={`${styles.tableContainer} ${className}`} ref={tableContainerRef}>
        {renderCards()}
        
        {/* ИСПРАВЛЕНО: Отображаем пагинацию всегда, если она должна быть показана */}
        {shouldShowPagination && (
          <div className={styles.paginationContainer}>
            <Pagination
              currentPage={currentPage}
              totalPages={calculatedTotalPages}
              totalItems={effectiveTotalItems}
              onPageChange={handlePaginationPageChange}
              pageSize={pageSize}
              onPageSizeChange={showPageSizeSelector ? handlePaginationSizeChange : undefined}
              itemName={itemName}
              disabled={false} // ИСПРАВЛЕНО: Убрана блокировка во время загрузки
              isMobile={isMobile}
              isTablet={isTablet}
            />
            {paginationAddons && (
              <div>
                {paginationAddons}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Обычный режим таблицы с адаптивными настройками
  return (
    <div 
      className={`${styles.tableContainer} ${className} ${enableHorizontalScroll ? 'overflow-x-auto' : ''}`} 
      ref={tableContainerRef}
    >
      <div 
        className={`${!autoHeight ? styles.scrollContainer : ''}`}
        style={getTableContainerStyle()}
      >
        <table className={styles.tableWrapper}>
          {!hideHeader && (
            <TableHeader 
              columns={visibleColumns}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort ? handleSort : undefined}
              onColumnResize={resizableColumns ? handleColumnResize : undefined}
              columnWidths={columnWidths}
              containerWidth={containerWidth}
              responsive={responsive}
              compactMode={useCompactMode}
            />
          )}
          <TableBody 
            data={displayedData}
            columns={visibleColumns}
            onRowClick={onRowClick}
            isLoading={isLoading}
            keyExtractor={keyExtractor}
            emptyText={emptyText || t('table.empty', { defaultValue: 'Нет данных' })}
            loadingText={loadingText || t('table.loading', { defaultValue: 'Загрузка...' })}
            columnWidths={columnWidths}
            containerWidth={containerWidth}
            responsive={responsive}
            compactMode={useCompactMode}
          />
        </table>
      </div>

      {/* ИСПРАВЛЕНО: Отображаем пагинацию всегда, если она должна быть показана */}
      {shouldShowPagination && (
        <div className={styles.paginationContainer}>
          <Pagination
            currentPage={currentPage}
            totalPages={calculatedTotalPages}
            totalItems={effectiveTotalItems}
            onPageChange={handlePaginationPageChange}
            pageSize={pageSize}
            onPageSizeChange={showPageSizeSelector ? handlePaginationSizeChange : undefined}
            itemName={itemName}
            disabled={false} // ИСПРАВЛЕНО: Убрана блокировка во время загрузки
            isMobile={isMobile}
            isTablet={isTablet}
          />
          {paginationAddons && (
            <div>
              {paginationAddons}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Table;